import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, Home, Clock, CheckCircle, XCircle, Edit, Trash2, Upload, X, ImageIcon, TrendingUp, Calendar, Users, Eye, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { SellerProperty, SalesStage, Seller } from '../types/seller';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { sellerPropertyAPI } from '../lib/seller-property-api';
import { imageUploadAPI } from '../lib/image-upload-api';
import { sellerAPI } from '../lib/seller-api';
import { authService } from '../lib/auth';
import { logger } from '../services/logger.service';
import { Property } from '../types/property';

// Helper: Convert SellerProperty → Property for the detail view
function sellerPropertyToProperty(sp: SellerProperty, sellerName?: string): Property {
  return {
    id: sp.id,
    slug: sp.id,
    title: sp.title,
    type: sp.propertyType,
    status: sp.status === 'approved' ? 'Published' : sp.status === 'pending' ? 'In-Review' : 'Draft',
    address: {
      street: sp.location?.address ?? '',
      suburb: sp.location?.city ?? '',
      city: sp.location?.city ?? '',
      province: sp.location?.province ?? '',
      postalCode: '',
    },
    geo: { lat: 0, lng: 0 },
    price: sp.price,
    facts: {
      bedrooms: sp.bedrooms,
      bathrooms: sp.bathrooms,
      parking: sp.parkingSpaces,
      floorSize: sp.size,
    },
    amenities: sp.features || [],
    media: (sp.images || []).map((url, i) => ({
      id: `img-${i}`,
      url,
      alt: `${sp.title} image ${i + 1}`,
      isCover: i === 0,
      order: i,
    })),
    description: sp.description,
    listingDate: sp.createdDate || new Date().toISOString(),
    agentId: '',
    visibility: 'Public',
    flags: {},
  };
}

interface SellerHubProps {
  onBackToCatalog: () => void;
  onPropertySelect?: (property: any) => void;
}

export function SellerHub({ onBackToCatalog, onPropertySelect }: SellerHubProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [currentSeller, setCurrentSeller] = useState<Seller | null>(null); // Load from backend
  const [properties, setProperties] = useState<SellerProperty[]>([]);
  const [activeTab, setActiveTab] = useState('properties');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'price_high' | 'price_low' | 'status'>('newest');
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]); // base64 for preview
  const [imageFiles, setImageFiles] = useState<File[]>([]);           // File objects for S3 upload
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // New property form state
  const [newProperty, setNewProperty] = useState({
    title: '',
    description: '',
    price: '',
    address: '',
    city: '',
    province: 'Gauteng',
    bedrooms: '2',
    bathrooms: '1',
    parkingSpaces: '1',
    size: '',
    propertyType: 'House' as const,
    listingType: 'For Sale' as const,
    features: '',
  });

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock authentication
    if (email && password) {
      setIsAuthenticated(true);
      toast.success('Successfully logged in!');
    } else {
      toast.error('Please enter email and password');
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    // Reset input so the same file can be re-selected if removed
    e.target.value = '';

    const newImages: string[] = [];
    const maxSize = 5 * 1024 * 1024; // 5MB per image
    const maxImages = 10;

    if (uploadedImages.length + files.length > maxImages) {
      toast.error(`You can upload a maximum of ${maxImages} images`);
      return;
    }

    Array.from(files).forEach((file) => {
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`);
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setUploadedImages(prev => [...prev, result]);
        setImageFiles(prev => [...prev, file]);
        toast.success(`${file.name} added`);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    toast.success('Image removed');
  };

  const handleAddProperty = async () => {
    if (!newProperty.title || !newProperty.price || !newProperty.address) {
      toast.error('Please fill in all required fields (Title, Price, Address)');
      return;
    }

    try {
      // Upload images to S3 first, collect URLs
      let imageUrls: string[] = [];
      if (imageFiles.length > 0) {
        toast.info(`Uploading ${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''}...`);
        const uploadResults = await Promise.allSettled(
          imageFiles.map(file => imageUploadAPI.uploadImage(file))
        );
        imageUrls = uploadResults
          .filter((r): r is PromiseFulfilledResult<{ url: string; path: string }> => r.status === 'fulfilled')
          .map(r => r.value.url);
        const failed = uploadResults.filter(r => r.status === 'rejected').length;
        if (failed > 0) toast.warning(`${failed} image${failed > 1 ? 's' : ''} failed to upload`);
      }

      // F-023: payload aligned with createProperty Lambda (POST /seller-properties)
      const createdProperty = await sellerPropertyAPI.createSellerProperty({
        sellerId: currentSeller!.id,
        address: newProperty.address,
        city: newProperty.city,
        state: newProperty.province,
        title: newProperty.title,
        description: newProperty.description,
        price: parseFloat(newProperty.price),
        bedrooms: parseInt(newProperty.bedrooms),
        bathrooms: parseInt(newProperty.bathrooms),
        parkingSpaces: parseInt(newProperty.parkingSpaces),
        size: parseFloat(newProperty.size) || 0,
        propertyType: newProperty.propertyType,
        listingType: newProperty.listingType,
        features: newProperty.features.split(',').map(f => f.trim()).filter(f => f),
        images: imageUrls,
        status: 'pending',
      } as any);

      // Optimistically prepend, then re-fetch to get server-side data
      setProperties([createdProperty, ...properties]);
      toast.success('Property submitted for approval');

      // Re-fetch to ensure list reflects actual DB state
      if (currentSeller) {
        try {
          const fresh = await sellerPropertyAPI.getSellerPropertiesBySeller(currentSeller.id);
          setProperties(fresh);
        } catch { /* keep optimistic update */ }
      }

      // Reset form
      setActiveTab('properties');
      setUploadedImages([]);
      setImageFiles([]);
      setNewProperty({
        title: '',
        description: '',
        price: '',
        address: '',
        city: '',
        province: 'Gauteng',
        bedrooms: '2',
        bathrooms: '1',
        parkingSpaces: '1',
        size: '',
        propertyType: 'House',
        listingType: 'For Sale',
        features: '',
      });
    } catch (error) {
      logger.error('Error creating property', error as Error, undefined, 'SellerHub');
      toast.error('Failed to create property. Please try again.');
    }
  };

  const getStatusBadge = (status: SellerProperty['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" /> Pending Review</Badge>;
      case 'approved':
        return <Badge variant="default" className="gap-1 bg-green-600"><CheckCircle className="h-3 w-3" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Rejected</Badge>;
      case 'archived':
        return <Badge variant="secondary" className="gap-1">Archived</Badge>;
    }
  };

  const getSalesStageInfo = (stage: SalesStage | undefined) => {
    if (!stage) return { label: 'Not Listed', progress: 0, color: 'bg-gray-400' };
    
    const stages = {
      'listed': { label: 'Listed', progress: 20, color: 'bg-blue-500' },
      'viewings-scheduled': { label: 'Viewings Scheduled', progress: 40, color: 'bg-yellow-500' },
      'offers-received': { label: 'Offers Received', progress: 60, color: 'bg-orange-500' },
      'under-offer': { label: 'Under Offer', progress: 80, color: 'bg-purple-500' },
      'sold': { label: 'Sold', progress: 100, color: 'bg-green-500' },
    };
    
    return stages[stage];
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    }
    
    return date.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getViewingStatusBadge = (status: 'scheduled' | 'completed' | 'cancelled') => {
    switch (status) {
      case 'scheduled':
        return <Badge variant="outline" className="text-xs">Scheduled</Badge>;
      case 'completed':
        return <Badge variant="secondary" className="text-xs">Completed</Badge>;
      case 'cancelled':
        return <Badge variant="destructive" className="text-xs">Cancelled</Badge>;
    }
  };

  useEffect(() => {
    const loadSellerData = async () => {
      setIsLoading(true);
      try {
        // Get the currently logged-in user from authService
        const loggedInUser = authService.getCurrentUser();

        if (!loggedInUser) {
          logger.error('No logged-in user found for SellerHub', new Error('Missing user'), undefined, 'SellerHub');
          setIsLoading(false);
          return;
        }

        // Fetch the seller profile from the backend using the logged-in user's ID
        let seller: Seller | null = null;
        try {
          seller = await sellerAPI.getSeller(loggedInUser.id);
        } catch (err) {
          console.warn('Unexpected error fetching seller profile:', err);
        }

        // If backend didn't return a profile (first-time Cognito seller),
        // create and persist the seller record so it's available on future loads.
        if (!seller) {
          const nameParts = (loggedInUser.name || '').trim().split(/\s+/);
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          const newSeller = {
            id: loggedInUser.id,
            name: loggedInUser.name,
            firstName,
            lastName,
            email: loggedInUser.email,
            phone: '',
            status: 'active' as const,
          } as unknown as Seller;

          try {
            seller = await sellerAPI.createSeller(newSeller);
          } catch (createErr) {
            console.warn('Could not persist seller profile, using local data:', createErr);
            seller = newSeller;
          }
        }

        setCurrentSeller(seller);

        // Fetch only this seller's properties
        try {
          const sellerProperties = await sellerPropertyAPI.getSellerPropertiesBySeller(seller.id);
          setProperties(sellerProperties);
        } catch (err) {
          logger.error('Error fetching seller properties', err as Error, undefined, 'SellerHub');
          setProperties([]);
        }


      } catch (error) {
        logger.error('Error loading seller data', error as Error, undefined, 'SellerHub');
      } finally {
        setIsLoading(false);
      }
    };

    loadSellerData();
  }, []);

  const refreshProperties = async () => {
    if (!currentSeller) return;
    setIsRefreshing(true);
    try {
      const sellerProperties = await sellerPropertyAPI.getSellerPropertiesBySeller(currentSeller.id);
      setProperties(sellerProperties);
    } catch (err) {
      toast.error('Failed to refresh properties');
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Seller Portal Login</CardTitle>
            <CardDescription>
              List your property with Something Property
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <Button type="submit" className="w-full" data-testid="seller-login-submit">
                Login
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                <p>Don't have an account?</p>
                <Button variant="link" className="p-0 h-auto" data-testid="seller-register-link">
                  Register as a Seller
                </Button>
              </div>

              <div className="pt-4 border-t">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={onBackToCatalog}
                  data-testid="seller-back-to-properties"
                >
                  Back to Properties
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!currentSeller) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-16 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading seller information...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="mb-2">Seller Portal</h1>
          <p className="text-muted-foreground">
            Welcome back, {currentSeller!.name}
          </p>
        </div>
        <div />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Listed</CardDescription>
            <CardTitle>{properties.length}</CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Approved</CardDescription>
            <CardTitle className="text-green-600">
              {Array.isArray(properties) ? properties.filter(p => p.status === 'approved').length : 0}
            </CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Pending Review</CardDescription>
            <CardTitle className="text-yellow-600">
              {Array.isArray(properties) ? properties.filter(p => p.status === 'pending').length : 0}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="properties">My Properties</TabsTrigger>
          <TabsTrigger value="add">Add New Property</TabsTrigger>
          <TabsTrigger value="offers">My Offers</TabsTrigger>
        </TabsList>

        <TabsContent value="properties" className="space-y-4">
          {properties.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Home className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  You haven't listed any properties yet
                </p>
                <Button onClick={() => setActiveTab('add')} data-testid="seller-property-create-first">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Property
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground">{properties.length} propert{properties.length === 1 ? 'y' : 'ies'}</p>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={refreshProperties} disabled={isRefreshing} title="Refresh" data-testid="seller-properties-refresh">
                    <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
                  <SelectTrigger className="w-44">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="price_high">Price: High to Low</SelectItem>
                    <SelectItem value="price_low">Price: Low to High</SelectItem>
                    <SelectItem value="status">By Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            <div className="grid gap-4">
              {[...properties].sort((a, b) => {
                if (sortBy === 'newest') return (b.createdAt as any) - (a.createdAt as any);
                if (sortBy === 'oldest') return (a.createdAt as any) - (b.createdAt as any);
                if (sortBy === 'price_high') return (b.price || 0) - (a.price || 0);
                if (sortBy === 'price_low') return (a.price || 0) - (b.price || 0);
                if (sortBy === 'status') return (a.status || '').localeCompare(b.status || '');
                return 0;
              }).map((property) => (
                <Card
                  key={property.id}
                  className="cursor-pointer hover:border-[#FDB022]/40 transition-colors"
                  onClick={() => onPropertySelect?.(sellerPropertyToProperty(property, currentSeller?.name))}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="flex-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); onPropertySelect?.(sellerPropertyToProperty(property, currentSeller?.name)); }}
                              className="text-left group/title"
                              data-testid="seller-property-title-link"
                            >
                              <h3 className="mb-1 group-hover/title:text-[#FDB022] transition-colors cursor-pointer inline-flex items-center gap-1.5">
                                {property.title}
                                <ExternalLink className="h-3.5 w-3.5 opacity-0 group-hover/title:opacity-100 transition-opacity" />
                              </h3>
                            </button>
                            <p className="text-muted-foreground text-sm mb-2">
                              {property.city || property.location?.city || '—'}
                            </p>
                            {getStatusBadge(property.status)}
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">
                              R {property.price?.toLocaleString() ?? '—'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {property.listingType}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-4 text-sm text-muted-foreground mb-3">
                          <span>{property.bedrooms} Beds</span>
                          <span>{property.bathrooms} Baths</span>
                          <span>{property.size} m²</span>
                          <span>{property.propertyType}</span>
                        </div>

                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                          {property.description}
                        </p>

                        {/* Sales Progress Tracker */}
                        {property.status === 'approved' && property.salesStage && (
                          <div className="p-4 bg-muted/30 rounded-lg border mb-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <TrendingUp className="h-4 w-4 text-primary" />
                                <span className="text-sm font-medium">Sales Progress</span>
                              </div>
                              <Badge variant="secondary" className="text-xs">
                                {getSalesStageInfo(property.salesStage).label}
                              </Badge>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2 mb-2">
                              <div 
                                className={`h-2 rounded-full transition-all ${getSalesStageInfo(property.salesStage).color}`}
                                style={{ width: `${getSalesStageInfo(property.salesStage).progress}%` }}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {getSalesStageInfo(property.salesStage).progress}% complete
                            </p>
                          </div>
                        )}

                        {/* Lead Count and Viewings Summary */}
                        {property.status === 'approved' && (
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            {property.leadCount !== undefined && (
                              <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                <div className="flex items-center gap-2 mb-1">
                                  <Users className="h-4 w-4 text-blue-600" />
                                  <span className="text-xs font-medium text-blue-900 dark:text-blue-100">Lead Inquiries</span>
                                </div>
                                <p className="text-2xl font-bold text-blue-600">{property.leadCount}</p>
                                <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">Total interested buyers</p>
                              </div>
                            )}

                            {property.viewings && property.viewings.length > 0 && (
                              <Dialog>
                                <DialogTrigger asChild>
                                  <button onClick={(e) => e.stopPropagation()} className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800 text-left hover:bg-purple-100 dark:hover:bg-purple-950/30 transition-colors" data-testid="seller-property-viewings-trigger">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Calendar className="h-4 w-4 text-purple-600" />
                                      <span className="text-xs font-medium text-purple-900 dark:text-purple-100">Property Viewings</span>
                                    </div>
                                    <p className="text-2xl font-bold text-purple-600">{property.viewings.length}</p>
                                    <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
                                      {property.viewings.filter(v => v.status === 'scheduled').length} upcoming
                                    </p>
                                  </button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Property Viewings</DialogTitle>
                                    <DialogDescription>
                                      Scheduled and past viewings for {property.title}
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-3 mt-4">
                                    {property.viewings.map((viewing) => (
                                      <div key={viewing.id} className="p-3 border rounded-lg">
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            <Eye className="h-4 w-4 text-muted-foreground" />
                                            <span className="font-medium text-sm">{formatDate(viewing.date)}</span>
                                            <span className="text-sm text-muted-foreground">{viewing.time}</span>
                                          </div>
                                          {getViewingStatusBadge(viewing.status)}
                                        </div>
                                        {viewing.feedback && (
                                          <p className="text-xs text-muted-foreground mt-2 p-2 bg-muted/30 rounded">
                                            <strong>Feedback:</strong> {viewing.feedback}
                                          </p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                          </div>
                        )}

                        {property.images && property.images.length > 0 && (
                          <div className="flex gap-2">
                            {property.images.slice(0, 4).map((image, idx) => (
                              <div key={idx} className="relative w-16 h-16">
                                <img
                                  src={image}
                                  alt={`${property.title} ${idx + 1}`}
                                  className="w-full h-full object-cover rounded border"
                                />
                              </div>
                            ))}
                            {property.images.length > 4 && (
                              <div className="w-16 h-16 rounded border bg-muted flex items-center justify-center text-sm">
                                +{property.images.length - 4}
                              </div>
                            )}
                          </div>
                        )}

                        {property.status === 'rejected' && property.rejectionReason && (
                          <div className="p-3 bg-destructive/10 rounded-md">
                            <p className="text-sm text-destructive">
                              <strong>Rejection Reason:</strong> {property.rejectionReason}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 text-xs"
                          onClick={(e) => { e.stopPropagation(); onPropertySelect?.(sellerPropertyToProperty(property, currentSeller?.name)); }}
                          data-testid="seller-property-view"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          View Listing
                        </Button>
                        <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); setEditingPropertyId(property.id); setNewProperty({ title: property.title, description: property.description || '', price: String(property.price || ''), address: property.address || property.location?.address || '', city: property.city || property.location?.city || '', province: property.state || property.location?.province || 'Gauteng', bedrooms: String(property.bedrooms || '2'), bathrooms: String(property.bathrooms || '1'), parkingSpaces: String(property.parkingSpaces || '1'), size: String(property.size || ''), propertyType: (property.propertyType || 'House') as 'House', listingType: (property.listingType || 'For Sale') as 'For Sale', features: Array.isArray(property.features) ? property.features.join(', ') : (property.features || '') }); setActiveTab('add'); }} data-testid="seller-property-edit">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="icon" onClick={(e) => e.stopPropagation()} data-testid="seller-property-delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="add">
          <Card>
            <CardHeader>
              <CardTitle>List a New Property</CardTitle>
              <CardDescription>
                Fill in the details below. Your listing will be reviewed by our team before going live.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="title">Property Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Modern Family Home in Sandton"
                    value={newProperty.title}
                    onChange={(e) => setNewProperty({ ...newProperty, title: e.target.value })}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your property in detail..."
                    rows={4}
                    value={newProperty.description}
                    onChange={(e) => setNewProperty({ ...newProperty, description: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Price (ZAR) *</Label>
                  <Input
                    id="price"
                    type="number"
                    placeholder="3500000"
                    value={newProperty.price}
                    onChange={(e) => setNewProperty({ ...newProperty, price: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="size">Size (m²) *</Label>
                  <Input
                    id="size"
                    type="number"
                    placeholder="250"
                    value={newProperty.size}
                    onChange={(e) => setNewProperty({ ...newProperty, size: e.target.value })}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    placeholder="123 Main Street"
                    value={newProperty.address}
                    onChange={(e) => setNewProperty({ ...newProperty, address: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    placeholder="Johannesburg"
                    value={newProperty.city}
                    onChange={(e) => setNewProperty({ ...newProperty, city: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="province">Province *</Label>
                  <Select
                    value={newProperty.province}
                    onValueChange={(value) => setNewProperty({ ...newProperty, province: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Gauteng">Gauteng</SelectItem>
                      <SelectItem value="Western Cape">Western Cape</SelectItem>
                      <SelectItem value="KwaZulu-Natal">KwaZulu-Natal</SelectItem>
                      <SelectItem value="Eastern Cape">Eastern Cape</SelectItem>
                      <SelectItem value="Free State">Free State</SelectItem>
                      <SelectItem value="Limpopo">Limpopo</SelectItem>
                      <SelectItem value="Mpumalanga">Mpumalanga</SelectItem>
                      <SelectItem value="Northern Cape">Northern Cape</SelectItem>
                      <SelectItem value="North West">North West</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="propertyType">Property Type *</Label>
                  <Select
                    value={newProperty.propertyType}
                    onValueChange={(value: any) => setNewProperty({ ...newProperty, propertyType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="House">House</SelectItem>
                      <SelectItem value="Apartment">Apartment</SelectItem>
                      <SelectItem value="Townhouse">Townhouse</SelectItem>
                      <SelectItem value="Land">Land</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="listingType">Listing Type *</Label>
                  <Select
                    value={newProperty.listingType}
                    onValueChange={(value: any) => setNewProperty({ ...newProperty, listingType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="For Sale">For Sale</SelectItem>
                      <SelectItem value="To Rent">To Rent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bedrooms">Bedrooms *</Label>
                  <Input
                    id="bedrooms"
                    type="number"
                    min="0"
                    value={newProperty.bedrooms}
                    onChange={(e) => setNewProperty({ ...newProperty, bedrooms: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bathrooms">Bathrooms *</Label>
                  <Input
                    id="bathrooms"
                    type="number"
                    min="0"
                    value={newProperty.bathrooms}
                    onChange={(e) => setNewProperty({ ...newProperty, bathrooms: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parking">Parking Spaces *</Label>
                  <Input
                    id="parking"
                    type="number"
                    min="0"
                    value={newProperty.parkingSpaces}
                    onChange={(e) => setNewProperty({ ...newProperty, parkingSpaces: e.target.value })}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="features">Features (comma separated)</Label>
                  <Input
                    id="features"
                    placeholder="Garden, Pool, Security, Fireplace"
                    value={newProperty.features}
                    onChange={(e) => setNewProperty({ ...newProperty, features: e.target.value })}
                  />
                </div>
              </div>

              {/* Image Upload Section */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Property Images <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <p className="text-sm text-muted-foreground">
                    Upload up to 10 images (Max 5MB each, JPG, PNG, WebP)
                  </p>
                  
                  <div
                    className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/heic"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      onClick={e => e.stopPropagation()}
                    />
                    <div className="flex flex-col items-center gap-2 pointer-events-none">
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Click to upload images</p>
                        <p className="text-sm text-muted-foreground">JPG, PNG, WebP — max 5MB each</p>
                      </div>
                      <Button type="button" variant="outline" size="sm" data-testid="seller-property-image-choose" className="pointer-events-none">
                        <ImageIcon className="h-4 w-4 mr-2" />
                        Choose Files
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Image Preview Grid */}
                {uploadedImages.length > 0 && (
                  <div>
                    <Label>Uploaded Images ({uploadedImages.length}/10)</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                      {uploadedImages.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image}
                            alt={`Property image ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleRemoveImage(index)}
                            data-testid="seller-property-image-remove"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          {index === 0 && (
                            <Badge className="absolute bottom-2 left-2 text-xs">
                              Main Photo
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button onClick={handleAddProperty} className="flex-1" data-testid="seller-property-submit">
                  <Plus className="h-4 w-4 mr-2" />
                  Submit for Approval
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setNewProperty({
                      title: '',
                      description: '',
                      price: '',
                      address: '',
                      city: '',
                      province: 'Gauteng',
                      bedrooms: '2',
                      bathrooms: '1',
                      parkingSpaces: '1',
                      size: '',
                      propertyType: 'House',
                      listingType: 'For Sale',
                      features: '',
                    });
                    setUploadedImages([]);
      setImageFiles([]);
                  }}
                  data-testid="seller-property-form-clear"
                >
                  Clear Form
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="offers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Offers Received</CardTitle>
              <CardDescription>Offers submitted by buyers on your listed properties</CardDescription>
            </CardHeader>
            <CardContent>
              {properties.filter(p => p.status === 'approved').length === 0 ? (
                <div className="text-center py-16">
                  <Home className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No approved properties yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Offers will appear here once your properties are approved and listed on the platform.
                  </p>
                  <Button variant="outline" onClick={() => setActiveTab('properties')}>
                    View My Properties
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {properties.filter(p => p.status === 'approved').map(property => (
                    <div key={property.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium">{property.title}</h4>
                          <p className="text-sm text-muted-foreground">
                            {property.city || '—'} • R{(property.price || 0).toLocaleString()}
                          </p>
                        </div>
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Listed
                        </Badge>
                      </div>
                      <div className="text-center py-6 text-muted-foreground bg-muted/30 rounded-lg">
                        <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm font-medium">No offers yet</p>
                        <p className="text-xs mt-1">Offers from buyers will appear here when submitted</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}